import React, { useState, useEffect, useCallback } from "react";
import { Typography, Grid, Button, Tooltip, Switch, Box, DialogContentText } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import Header from "../../Header/Header";
import ProvisionTreeV2 from "../components/ProvisionTreeV2";
import CustomerSelector from "../../Common/CustomerSelector";
import SiteSelector from "../../Common/SiteSelector";
import ProductSelectList from "../../Common/Selectors/ProductSelectList";
import CustomerCard from "../../Common/CustomerCard";
import provisionService from "../../../services/ProvisionService";
import { useTheme } from "@mui/material/styles";
import { getGenericStyles } from "../../../theme/styles";
import AcademicYearSelector from "../../Common/AcademicYearSelector";
import {
  ProductDTO,
  AcademicYearDTO,
  SiteDTO,
  ProvisionedAuditLog,
  ProvisionAction,
} from "@beable/roster-backend-client-v1";
import BasePanel from "../../Common/Layout/BasePanel";
import { BasePanelBox } from "../../Common/Layout/BasePanelBox";
import { StyledPanelHeader } from "../../Common/Layout/PanelHeader";
import BaseSheet from "../../Common/Layout/BaseSheet";
import { formatLogText } from '../../../util/formatLogText';
import { CustomerDTO, ProvisionTreeNodeDTO } from "@beable/roster-backend-client-v1";
import ProvisionTreeSelections from "../../ProvisionTreeSelections/ProvisionTreeSelections";
import BaseDialog from "../../Common/BaseDialog";

export default function Provision() {
  const theme = useTheme();
  const styles = getGenericStyles(theme);

  const [customer, setCustomer] = useState<CustomerDTO>();
  const [site, setSite] = useState<SiteDTO>();
  const [academicYear, setAcademicYear] = useState<AcademicYearDTO>();
  const [product, setProduct] = useState<ProductDTO>();

  const [rosterWarning, setRosterWarning] = useState("");
  const [nodes, setNodes] = useState<any[]>([]);
  const [checkedNodes, setCheckedNodes] = useState<any[]>([]);
  const [preSelectedNodes, setPreSelectedNodes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isNodesLoading, setIsNodesLoading] = useState(false);
  const [logInfo, setLogInfo] = useState<ProvisionedAuditLog | undefined>();
  const [isColumnView, setIsColumnView] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState('');
  const [resultMessage, setResultMessage] = useState('');
  const [hasResult, setHasResult] = useState(false);

  const clearNodes = () => {
    setNodes([]);
    setCheckedNodes([]);
    setPreSelectedNodes([]);
  }

  const handleCustomerChange = (customer: CustomerDTO | undefined) => {
    resetSelections();
    setCustomer(customer);
    clearNodes();
  }

  const resetSelections = () => {
    setSite(undefined);
    setAcademicYear(undefined);
  }

  const handleSiteChange = (site: SiteDTO | undefined) => {
    clearNodes();
    setAcademicYear(undefined);
    setSite(site);
  }

  const handleNodeCheck = (data: any[]) => {
    setCheckedNodes([...data]);
    setPreSelectedNodes([...data]);
  };

  const handleProductChange = (product: ProductDTO) => {
    clearNodes();
    setProduct(product);
  }

  const handleAcademicYearChange = (academicYear: AcademicYearDTO | undefined) => {
    clearNodes();
    setAcademicYear(academicYear);
  }

  async function getNodeChildren(node: ProvisionTreeNodeDTO) {
    const data = await provisionService.getProvisionNodes(site!.id, academicYear!.academicYearId, node.value);
    return data;
  }

  async function getRootNode(node: ProvisionTreeNodeDTO, appendChildren?: boolean) {
    const data = await provisionService.getRootNode(site!.id, academicYear!.academicYearId, node.value, appendChildren);
    return data;
  }

  useEffect(() => {
    const fetchNodeData = async () => {
      // const data = await provisionService.getNodes(site!.id, academicYear!.academicYearId);
      const roots = await provisionService.getProvisionNodes(site!.id, academicYear!.academicYearId);
      let data;

      if (roots?.length) {
        const progressUnit = 100 / roots.length;
        let progress = 0;
        for (const root of roots) {
          const rootWithChildren = await provisionService.getRootNode(site!.id, academicYear!.academicYearId, root.value, true);
          root.children = rootWithChildren?.children;
          progress += progressUnit;
          setLoadingProgress(`${progress.toFixed(2)}%`);
        }
        data = roots;
      }
      setLoadingProgress('');

      if (data && data[0] && data[0].warning) {
        setRosterWarning(data[0].warning);
      } else if (!data) {
        setRosterWarning(
          "Error while getting the roster data. Please contact our support..."
        );
      } else if (data) {
        setNodes(data);
        setRosterWarning("");
      }
    };

    const fetchSelectionData = async () => {
      const data = await provisionService.getPreSelectedNodes(site!.id, academicYear!.academicYearId, product!.id);
      if (data) {
        // TODO: remove this check. Added for backwards compatability
        if (Array.isArray(data)) {
          setCheckedNodes(data);
          setPreSelectedNodes(data);
          return;
        }
        setCheckedNodes(data.selections);
        setPreSelectedNodes(data.selections);
        setLogInfo(data.log)
      }
    };

    const fetchData = async () => {
      setIsNodesLoading(true);
      await fetchSelectionData();
      await fetchNodeData();
      setIsNodesLoading(false);
    }

    if (site?.id && academicYear?.academicYearId && product?.id) {
      fetchData();
    }
  }, [site, academicYear, product]);

  const handleSubmit = () => {
    const submitData = async () => {
      setIsLoading(true);
      const uniqueNodes = checkedNodes.slice(0, checkedNodes.length);
      const pipeNodes = checkedNodes
        .slice(0, checkedNodes.length)
        .filter((i) => i.includes("|"));
      const splicePositions: number[] = [];
      pipeNodes.forEach((el) => {
        const cleanId = el.split("|")[0];
        if (uniqueNodes.find((i) => i === cleanId)) {
          const pos = uniqueNodes.indexOf(cleanId);
          splicePositions.push(pos);
        }
      });

      const spliceablePositionsReversed = Array.from(
        new Set(splicePositions)
      ).sort((a, b) => b - a);
      spliceablePositionsReversed.forEach((position) => {
        uniqueNodes.splice(position, 1);
      });
      const data = await provisionService.saveSelectedNodes(
        site!.id,
        academicYear!.academicYearId,
        product!.id,
        uniqueNodes
      );
      if (data) {
        setIsLoading(false);
        setResultMessage('The selections were saved successfully!');
        setHasResult(true);
      } else {
        setIsLoading(false);
        setResultMessage('There was an error saving the selections');
        setHasResult(true);
      }
    };

    if (
      academicYear &&
      academicYear.schoolYear >= new Date(Date.now()).getFullYear()
    ) {
      submitData();
    } else {
      alert("You cannot make selections for this academic year. It has ended.");
    }
  };

  const provisionForAcademicYear = async () => {
    setIsLoading(true);
    const data = await provisionService.saveSelectedNodes(site!.id, academicYear!.academicYearId, product!.id, checkedNodes);
    if (data) {
      const provisionData = await provisionService.provisionAcademicYear(site!.id, academicYear!.academicYearId, product!.id, ProvisionAction.Provision);
      if (provisionData) {
        setIsLoading(false);
        setResultMessage('Successfully began the provisioning process.');
        setHasResult(true);
      } else {
        setIsLoading(false);
        setResultMessage('There was an error provisioning the records.');
        setHasResult(true);
      }
    } else {
      setIsLoading(false);
      setResultMessage('There was an error saving the selections');
      setHasResult(true);
    }
  };

  const resetResult = () => {
    setResultMessage('');
    setHasResult(false);
  }

  const memoHandleSelectionsChange = useCallback(handleNodeCheck, [checkedNodes, preSelectedNodes, isColumnView]);

  return (
    <>
      <Header />
      <Grid container>
        <Box
          component="form"
          sx={styles.root}
          data-testid="test-provisionForm"
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <BasePanel>
            <BasePanelBox>
              <StyledPanelHeader header="Provision user accounts" />
              <Grid container size={12}>
                <CustomerSelector
                  onSelect={handleCustomerChange}
                />
                <br />
                <SiteSelector
                  customerId={customer?.id ?? ''}
                  onSelect={handleSiteChange}
                />
                <br />
                <ProductSelectList
                  productId={product?.id ?? ''}
                  customerId={customer?.id ?? ''}
                  siteId={site?.id ?? 0}
                  onChange={handleProductChange}
                />
                <br />
                <AcademicYearSelector
                  siteId={site ? site.id : 0}
                  handleSelection={handleAcademicYearChange}
                  clearAcademicYear={!academicYear}
                  disabled={!site}
                />
                <br />
                <Grid sx={{ marginLeft: theme.spacing(2) }} size={11}>
                  <CustomerCard customer={customer} />
                </Grid>
                <br />
                <br />
              </Grid>
            </BasePanelBox>
          </BasePanel>
          <BaseSheet id="user-search-container" maxWidth="lg">
            <Grid size={12}>
              <Typography variant="h6" sx={styles.stepContainer_header}>
                Provision Users
                {logInfo && <Tooltip title={formatLogText(logInfo)}>
                  <InfoIcon color="primary" sx={styles.infoIcon} />
                </Tooltip>}
              </Typography>
              <Typography
                variant="body1"
                component="p"
                sx={styles.customerCard_content}
              >
                Users who meet your rule criteria will have user accounts <br />
              </Typography>
              <Typography
                variant="body2"
                component="span"
                sx={styles.customerCard_content}
              >
                <Switch
                  color="primary"
                  checked={isColumnView}
                  onChange={() => setIsColumnView(!isColumnView)}
                />
                {isColumnView ? 'Column View' : 'Tree View'}
              </Typography>
              {
                isColumnView ?
                  <ProvisionTreeSelections
                    nodes={nodes}
                    preSelectedNodeValues={preSelectedNodes}
                    onSelectionsChange={(newSelectedNodes) => memoHandleSelectionsChange(newSelectedNodes)}
                    getNodeChildren={getNodeChildren}
                    getRootNode={getRootNode}
                    rosterWarning={rosterWarning}
                    isLoading={isNodesLoading}
                    loadingProgress={loadingProgress}
                  />
                  :
                  <ProvisionTreeV2
                    nodes={nodes}
                    preSelectedNodeValues={preSelectedNodes}
                    onChange={(newSelectedNodes) => memoHandleSelectionsChange(newSelectedNodes)}
                    rosterWarning={rosterWarning}
                    isLoading={isNodesLoading}
                    loadingProgress={loadingProgress}
                  />
              }
              <Grid
                container
                spacing={2}
                sx={[{
                  justifyContent: "center"
                }, styles.grow]}>
                <Grid
                  key="0"
                  size={{
                    xs: 8,
                    sm: 4
                  }}>
                  <Button
                    data-testid="test-saveSelections"
                    variant={"contained"}
                    color={"primary"}
                    sx={styles.checkboxSubmit}
                    onClick={() => {
                      handleSubmit();
                    }}
                    disabled={
                      !site?.id ||
                      !academicYear?.academicYearId ||
                      rosterWarning.length > 0 ||
                      preSelectedNodes?.length === 0 ||
                      isLoading
                    }
                  >
                    Save Selections
                  </Button>
                </Grid>
                <Grid
                  key="2"
                  size={{
                    xs: 8,
                    sm: 4
                  }}>
                  <Button
                    variant={"contained"}
                    color={"primary"}
                    sx={styles.checkboxSubmit}
                    onClick={provisionForAcademicYear}
                    disabled={
                      !site?.id ||
                      !academicYear?.academicYearId ||
                      rosterWarning.length > 0 ||
                      isLoading
                    }
                  >
                    Provision Now
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </BaseSheet>
        </Box>
      </Grid>
      <BaseDialog
        open={hasResult}
        showCloseIcon={false}
        showCloseButton={true}
        onClose={() => resetResult()}
        maxWidth='xs'
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center"
          }}>
          <Box sx={{
            width: "100%"
          }}>
            <DialogContentText>
              {resultMessage}
            </DialogContentText>
          </Box>
        </Box>
      </BaseDialog>
    </>
  );
}
